import { ValidatorConstraint, ValidatorConstraintInterface } from 'class-validator';

export const ESTABLISHMENT_WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

export type EstablishmentWeekday = (typeof ESTABLISHMENT_WEEKDAYS)[number];

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** A missing day means unknown, not closed. close may be after midnight. */
export type EstablishmentHoursDay = {
    closed?: boolean;
    open?: string;
    close?: string;
};

export type EstablishmentHours = Partial<
    Record<EstablishmentWeekday, EstablishmentHoursDay | null>
>;

@ValidatorConstraint({ name: 'establishmentHours', async: false })
export class EstablishmentHoursConstraint implements ValidatorConstraintInterface {
    validate(value: unknown): boolean {
        if (value == null) return true;
        if (typeof value !== 'object' || Array.isArray(value)) return false;

        const record = value as Record<string, unknown>;

        for (const [key, day] of Object.entries(record)) {
            if (!(ESTABLISHMENT_WEEKDAYS as readonly string[]).includes(key)) return false;
            if (day == null) continue;
            if (typeof day !== 'object' || Array.isArray(day)) return false;

            const row = day as Record<string, unknown>;

            for (const field of Object.keys(row)) {
                if (field !== 'closed' && field !== 'open' && field !== 'close') return false;
            }

            if (row.closed != null && typeof row.closed !== 'boolean') return false;
            if (row.closed === true) continue;

            const openSet = row.open != null && row.open !== '';
            const closeSet = row.close != null && row.close !== '';

            if (openSet !== closeSet) return false;
            if (openSet && (typeof row.open !== 'string' || !HHMM.test(row.open))) return false;
            if (closeSet && (typeof row.close !== 'string' || !HHMM.test(row.close))) return false;
        }

        return true;
    }

    defaultMessage(): string {
        return 'hours must use weekday keys with closed, or open and close as HH:mm';
    }
}

/** Drop unknown days and blank slots. Null means we do not know the week. */
export const normalizeEstablishmentHours = (
    value: EstablishmentHours | null | undefined,
): EstablishmentHours | null => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;

    const out: EstablishmentHours = {};

    for (const day of ESTABLISHMENT_WEEKDAYS) {
        const raw = value[day];

        if (!raw || typeof raw !== 'object') continue;
        if (raw.closed) {
            out[day] = { closed: true };
            continue;
        }

        const open = raw.open?.trim().slice(0, 5);
        const close = raw.close?.trim().slice(0, 5);

        if (open && close && HHMM.test(open) && HHMM.test(close)) {
            out[day] = { open, close };
        }
    }

    return Object.keys(out).length ? out : null;
};
